/* eslint-disable @typescript-eslint/no-unused-vars */
import { unwrapResult } from '@reduxjs/toolkit';
import { Row, Col, Button, Checkbox } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, SubmitButton } from 'formik-antd';
import { useScrollTop } from 'hooks';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalWarningLeavePage } from 'components';
import { AppDispatch, RootState, t_cpap_instructions } from 'types';
import { medicalInstitution } from '../../../containers/App/selectors';
import { newOrder } from '../thunks';
import Wrap from './styles';

type Props = {
  nextStep: (itemId?: string) => void;
  stepToPrevious: () => void;
  error: () => void;
};

const RegisterThirdStep: FC<Props> = ({ nextStep, stepToPrevious, error }) => {
  useScrollTop();

  const dispatch = useDispatch<AppDispatch>();
  const medicalInstitutionList = useSelector(medicalInstitution);
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);
  const [fieldBirthDay, setFieldBirthDay] = useState<string>();
  const [spPrice, setSpPrice] = useState<string[]>([]);

  const { medical, cpapInstructions, dataStoreFields } = useSelector((state: RootState) => state.register);

  useEffect(() => {
    function calculate_age(dob: any) {
      const diff_ms = Date.now() - dob.getTime();
      const age_dt = new Date(diff_ms);

      return Math.abs(age_dt.getUTCFullYear() - 1970);
    }
    if (cpapInstructions.patient_birthday) {
      const year = cpapInstructions.patient_birthday.slice(0, 4);
      const month = cpapInstructions.patient_birthday.slice(4, 6);
      const date = cpapInstructions.patient_birthday.slice(6, 8);

      const age = calculate_age(new Date(Number(year), Number(month), Number(date)));

      setFieldBirthDay(`${year}年${month}月${date}日（${age}歳）`);
    }
  }, [cpapInstructions]);

  const medicalInfo = useMemo(() => {
    return medicalInstitutionList.find((f) => f.i_id === medical.id);
  }, [medicalInstitutionList, medical]);

  const selectAccessories = useMemo(() => {
    return dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'accessories')[1].options || [];
  }, [dataStoreFields]);

  const displayGender = useMemo(() => {
    const options: any[] =
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'gender')[1].options || [];
    const gender = options.find((f) => f.option_id === cpapInstructions.gender);

    return gender ? gender.value : '';
  }, [dataStoreFields, cpapInstructions.gender]);

  const accMax = useMemo(() => {
    const acc =
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'acc_mask')[1].options || [];
    return acc.find((f: any) => f.display_id === cpapInstructions.acc_mask) || null;
  }, [dataStoreFields, cpapInstructions]);

  const MachineSelected = useMemo(() => {
    const machines =
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'machine')[1].options || [];
    return machines.find((f: any) => f.display_id === cpapInstructions.machine) || null;
  }, [dataStoreFields, cpapInstructions]);

  const maxSize = useMemo(() => {
    const sizes =
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'acc_mask_size')[1].options || [];
    return sizes.find((f: any) => f.display_id === cpapInstructions.acc_mask_size);
  }, [dataStoreFields, cpapInstructions]);

  const handleSubmit = async (values: t_cpap_instructions, form: FormikHelpers<t_cpap_instructions>) => {
    const newPatientBirthDay =
      cpapInstructions.patient_birthday && cpapInstructions.patient_birthday.length === 8
        ? ` ${cpapInstructions.patient_birthday?.slice(0, 4)}/
      ${cpapInstructions.patient_birthday?.slice(4, 6)}/
      ${cpapInstructions.patient_birthday?.slice(6)}`
        : undefined;

    const currentYear = new Date().getFullYear();
    const collectionDate = cpapInstructions.collection_date
      ? moment(
          `${currentYear}-${cpapInstructions.collection_date?.slice(0, 2)}-${cpapInstructions.collection_date?.slice(
            2,
          )}`,
          'YYYY-MM-DD',
        ).format('YYYY-MM-DDThh:mm:ss')
      : undefined;

    let newItem: any = {};
    newItem = newPatientBirthDay && {
      ...newItem,
      patient_birthday: moment(newPatientBirthDay).format('YYYY-MM-DDThh:mm:ss'),
    };
    if (collectionDate) {
      newItem = { ...newItem, collection_date: collectionDate };
    }

    const newAccessories = cpapInstructions?.accessories
      ? [selectAccessories[0].display_id, ...cpapInstructions?.accessories]
      : [selectAccessories[0].display_id];

    const params = {
      item: {
        ...cpapInstructions,
        ...newItem,
        accessories: newAccessories,
        facility_name: medical.id,
        machine: MachineSelected && MachineSelected.option_id,
        acc_mask: accMax && accMax.option_id,
        sp_price: [cpapInstructions.sp_price],
      },
      return_item_result: true,
      appId: currentApplicationId || '',
      dataStoreId: 't_cpap_instructions',
    };
    const resultAction = await dispatch(newOrder(params));

    try {
      if (newOrder.fulfilled.match(resultAction)) {
        form.setSubmitting(false);
        nextStep(resultAction.payload.item_id);
      } else {
        error();
      }
    } catch (e) {
      console.log('Error :', unwrapResult(resultAction));
    }
  };

  const checkAccessories = useCallback(
    (value: string): boolean => {
      if (cpapInstructions.accessories) return cpapInstructions.accessories?.findIndex((f: any) => f === value) > -1;
      else return false;
    },
    [cpapInstructions.accessories],
  );

  return (
    <Wrap>
      <div>
        <span className="standardFont ※">※</span>
        <span className="standardFont">は必須項目</span>
      </div>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValue}
        render={() => (
          <Form>
            <div>
              <div className="title">
                <div className="rec" />
                <p className="largeBoldFont textTitle">医療機関</p>
              </div>
              <div className="form">
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">医療機関名</p>
                  </Col>
                  <Col span={19}>
                    <p className="middleBoldFont">{medicalInfo?.facility_name}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">所在地</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{`${
                      medicalInfo?.facility_zip ? `〒${medicalInfo?.facility_zip}` : ''
                    }  ${medicalInfo?.facility_address1 || ''}  ${medicalInfo?.facility_address2 || ''}`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">TEL</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{medicalInfo?.facility_tel}</p>
                  </Col>
                </Row>
                <Row>
                  <Col className="d-flex" span={5}>
                    <p className="standardBoldFont spaceLine">営業担当</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.in_charge_of_sales}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">事務担当</p>
                  </Col>
                  <Col span={9}>
                    <p className="standardFont">{cpapInstructions.clerk}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont">転院履歴</p>
                  </Col>
                  <Col span={9}>
                    <p className="standardFont">{cpapInstructions.transfer_history}</p>
                  </Col>
                </Row>
              </div>
            </div>
            <div>
              <div className="title">
                <div className="rec" />
                <p className="largeBoldFont textTitle">療養者</p>
              </div>
              <div className="form">
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">お名前</p>
                  </Col>
                  <Col span={19}>
                    <p className="middleBoldFont">{`${cpapInstructions.patient_name} (${cpapInstructions.patient_name_kana})`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">患者ID</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.patient_id}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">性別</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{displayGender}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">生年月日</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{fieldBirthDay}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">設置住所</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{`〒${cpapInstructions.zip_code} ${cpapInstructions.address_1} ${cpapInstructions.address_2}`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont">TEL</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">
                      {cpapInstructions.phone_number ||
                        (cpapInstructions.mobile_phone_number && `【携帯】${cpapInstructions.mobile_phone_number}`)}
                      {cpapInstructions.phone_number && cpapInstructions.mobile_phone_number
                        ? `/ 【携帯】 ${cpapInstructions.mobile_phone_number} `
                        : ''}
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5} className="standardBoldFont" style={{ marginTop: 5 }}>
                    特別料金対象者
                  </Col>
                  <Col span={19}>
                    <Form.Item name="sp_price">
                      <Checkbox value={spPrice} checked={!!cpapInstructions.sp_price}>
                        対象
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            <div>
              <div className="title">
                <div className="rec" />
                <p className="largeBoldFont textTitle">設 定</p>
              </div>
              <div className="form1">
                <Row>
                  <Col span={5} className="alignCenter mb-24">
                    <span className="standardBoldFont">基本設定</span>
                  </Col>
                  <Col span={19} className="d-flex">
                    <span className="space1 standardFont">圧力</span>
                    <p className="space1 standardFont">
                      {cpapInstructions.pressure_min && cpapInstructions.pressure_max
                        ? `${cpapInstructions.pressure_min} ~ ${cpapInstructions.pressure_max}`
                        : ''}
                    </p>
                    <span className="space1 standardFont">cmH2O</span>
                    <div className="l space2" />
                    <span className="space1 standardFont">ランプ時間</span>
                    <p className="space1 standardFont">
                      {cpapInstructions.lamp_time ? cpapInstructions.lamp_time : ''}
                    </p>
                    <span className="space1 standardFont">分</span>
                    <div className="l space2" />
                    <span className="space1 standardFont">最大ランプ時間</span>
                    <p className="space1 standardFont">
                      {cpapInstructions.lamp_time_max ? cpapInstructions.lamp_time_max : ''}
                    </p>
                    <span className="space1 standardFont">分</span>
                  </Col>
                </Row>
              </div>
              <div className="form">
                <Row>
                  <Col span={5} className="itemCenter">
                    <span className="standardBoldFont">機種</span>
                  </Col>
                  <Col span={19}>
                    <Row style={{ paddingBottom: '6px' }}>
                      <Col span={2}>
                        <span className="middleBoldFont">{MachineSelected && MachineSelected.value}</span>
                      </Col>
                      <Col span={7}>
                        <span>{cpapInstructions.machine === 'o_other' && cpapInstructions.machine_other}</span>
                      </Col>
                      <Col span={2}>機番</Col>
                      <Col span={7}>
                        <span>{cpapInstructions.machine_no}</span>
                      </Col>
                    </Row>
                    <Row className="bb-1">
                      <Col span={2}>
                        <span className="middleBoldFont">EPR</span>
                      </Col>
                      <Col span={5}>
                        <span>{cpapInstructions.machine_epr_no}</span>
                      </Col>
                      <Col span={2} className="middleBoldFont">
                        DN/キー
                      </Col>
                      <Col span={5}>
                        <span>{cpapInstructions.machine_dn_no}</span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <div className="form">
                <Row>
                  <Col span={5} className="itemCenter">
                    <span className="standardBoldFont">付属品</span>
                  </Col>
                  <Col span={19}>
                    <Row className="bb-1 mb-6px">
                      <Col span={3} className="d-flex alignCenter middleBoldFont">
                        <span className="standardFont">{selectAccessories[0].value}</span>
                      </Col>
                      <Col span={8} className="d-flex alignCenter">
                        <span className="standardFont">種類</span>
                        <span className="ml-15px standardFont">
                          {cpapInstructions.acc_mask !== 'o_other'
                            ? accMax && accMax.value
                            : cpapInstructions.acc_mask_other}
                        </span>
                      </Col>
                      <Col span={8} className="alignCenter">
                        <div className="l space2" />
                        <span className="ml-15px standardFont">サイズ</span>
                        <span className="ml-15px standardFont">
                          {cpapInstructions.acc_mask_size !== 'o_other'
                            ? maxSize && maxSize.value
                            : cpapInstructions.acc_mask_size_other}
                        </span>
                      </Col>
                    </Row>
                    <Row className="bb-1">
                      <Col
                        span={3}
                        className={
                          checkAccessories(selectAccessories[1].display_id)
                            ? `d-flex alignCenter middleBoldFont`
                            : 'd-flex alignCenter accessories-disable'
                        }
                      >
                        <span className="standardFont">{selectAccessories[1].value}</span>
                      </Col>
                      {checkAccessories(selectAccessories[1].display_id) && (
                        <Col span={5}>
                          <span className="standardFont">加湿器機番</span>
                          <span className="ml-15px standardFont">{cpapInstructions.humidifier_machine_no ?? ''}</span>
                        </Col>
                      )}
                      <Col
                        span={3}
                        className={
                          checkAccessories(selectAccessories[2].display_id)
                            ? `d-flex alignCenter middleBoldFont`
                            : 'd-flex alignCenter accessories-disable'
                        }
                      >
                        <span className="standardFont">{selectAccessories[2].value}</span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <div className="form2">
                    <Row>
                      <Col span={7} className="standardBoldFont  alignCenter">
                        次回定期送付月
                      </Col>
                      <Col span={17}>
                        <div className="d-flex alignCenter">
                          <p className="standardFont m-5px">
                            {moment(
                              `${cpapInstructions.last_month?.slice(0, 4)}-${cpapInstructions.last_month?.slice(4)}`,
                              'yyyy-MM',
                            ).format('YYYY年MM月') === 'Invalid date'
                              ? ''
                              : moment(
                                  `${cpapInstructions.last_month?.slice(0, 4)}-${cpapInstructions.last_month?.slice(
                                    4,
                                  )}`,
                                  'yyyy-MM',
                                ).format('YYYY年MM月')}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="form2">
                    <Row>
                      <Col span={7} className="standardBoldFont  alignCenter">
                        回収日
                      </Col>
                      <Col span={17}>
                        <div className="d-flex alignCenter">
                          <p className="standardFont m-5px">
                            {cpapInstructions.collection_date
                              ? `${cpapInstructions.collection_date?.slice(
                                  0,
                                  2,
                                )}月${cpapInstructions.collection_date?.slice(2)}日`
                              : ''}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont alignCenter">
                    設置日
                  </Col>
                  <Col span={19}>
                    <Row>
                      <Col span={8} className="alignCenter">
                        <span className="standardFont">設置日</span>
                        <span className="ml-5px standardFont">
                          {moment(cpapInstructions.installation_date, 'yyyy-MM-DDThh:mm:ss').format(
                            'YYYY年MM月DD日',
                          ) === 'Invalid date'
                            ? ''
                            : moment(cpapInstructions.installation_date, 'yyyy-MM-DDThh:mm:ss').format(
                                'YYYY年MM月DD日',
                              )}
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont d-flex alignCenter">
                    備考
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.cpap_remark}</p>
                  </Col>
                </Row>
              </div>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont d-flex alignCenter">
                    受診明細用備考
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.remark_for_output}</p>
                  </Col>
                </Row>
              </div>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont d-flex alignCenter">
                    利用停止
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.suspension_flg === 1 ? '利用停止' : ''}</p>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="submit">
              <div className="title">
                <p className="standardBoldFont mb-0">以上の内容で登録してよろしいですか。</p>
              </div>
              <div className="alignCenter">
                <Button onClick={stepToPrevious} className="back">
                  戻る
                </Button>
                <SubmitButton className="btnDefault btn">登録する</SubmitButton>
              </div>
            </div>
          </Form>
        )}
      />
      <ModalWarningLeavePage />
    </Wrap>
  );
};
const initialValue = {};

export default RegisterThirdStep;
